import React, { useEffect, useState } from 'react';
import Modal from 'components/Modal';
import messages from './Messages';
import Input from 'components/Input';
import MemberSignature from '../MemberSignature';
import Button from 'components/Button';
import { connect, MapDispatchToProps } from 'react-redux';
import { englishNumber, convertDigitToProperFormat } from 'utils/number';
import Select from 'components/Select';
import { IOption } from 'components/Select/Select';
import Badge from 'components/Badge';
import { BadgeGroup } from 'components/TaskListView/styles';
import { smartDateString } from 'utils/date';
import Icon from 'components/Icon';
import { black, red } from 'utils/style/colors';
import notify from 'components/Notify';
import { display } from 'components/ErrorView/ErrorDialog';
import { withTitle } from 'components/withTitle';
import Collapsible from 'components/Collapsible';
import {
  CompanyMemberLog,
  ICompany,
  ICompanyMember,
  ICompanyRole,
  IProjectMemberView,
  IUserRoleView,
  TProjects,
} from 'store/companies/companiesTypes';
import { IUser } from 'store/users/usersType';
import { appDispatch, IUsers } from 'store/store';
import {
  addMemberSignature,
  addMemberSignatureFn,
  addMemberToCompany,
  addMemberToCompanyFn,
  addUserRoles,
  addUserRolesFn,
  deleteUserRoles,
  deleteUserRolesFn,
  exportMembers,
  exportMembersFn,
  getCompanyMember,
  getCompanyMemberFn,
  getCompanyMemberLogs,
  getCompanyMemberLogsFn,
  removeMemberFromCompany,
  removeMemberFromCompanyFn,
  removeMemberSignature,
  removeMemberSignatureFn,
  removeProjectMember,
  removeProjectMemberFn,
  updateCompanyMember,
  updateCompanyMemberFn,
  updateProjectMember,
  updateProjectMemberFn,
} from 'store/companies/companiesSlice';
import { changeUserPhone, changeUserPhoneFn } from 'store/users/usersSlice';
import i18n, { languageDirection } from '../../../../i18n';

interface ComponentProps {
  companyMember: ICompanyMember;
  isSupport: boolean;
  companyObject: ICompany;
  user: IUser;
  onClose: () => void;
  projectMembers: IProjectMemberView[];
  companyRoles: { [p: number]: ICompanyRole };
  companyMembersMap: { [p: number]: ICompanyMember };
  users: IUsers;
  projects: TProjects;
}

const modalTabs = [
  { id: 'info', title: i18n.t('memberInfo', { ns: 'companyManagement' }) },
  { id: 'projects', title: i18n.t('projects', { ns: 'companyManagement' }) },
  { id: 'changeLog', title: i18n.t('changeLog', { ns: 'companyManagement' }) },
];

interface IDispatchToProps {
  changePhone: (
    userId: number,
    currentPhone: string | null,
    newPhone: string | null,
    newName: string
  ) => ReturnType<typeof changeUserPhoneFn>;
  addRoles: (
    userRoles: IUserRoleView[],
    companyId: number
  ) => ReturnType<typeof addUserRolesFn>;
  deleteRoles: (
    userRoles: IUserRoleView[],
    companyId: number
  ) => ReturnType<typeof deleteUserRolesFn>;
  addSignature: (
    companyId: number,
    userId: number,
    file: any
  ) => ReturnType<typeof addMemberSignatureFn>;
  deleteSignature: (
    companyId: number,
    userId: number
  ) => ReturnType<typeof removeMemberSignatureFn>;
  updateMember: (
    member: ICompanyMember
  ) => ReturnType<typeof updateCompanyMemberFn>;
  getMember: (
    userId: number,
    companyId: number
  ) => ReturnType<typeof getCompanyMemberFn>;
  getMemberLogs: (
    userId: number,
    companyId: number,
    befroId: number | null,
    count: number
  ) => ReturnType<typeof getCompanyMemberLogsFn>;
  addMember: (
    member: ICompanyMember
  ) => ReturnType<typeof addMemberToCompanyFn>;
  removeProjectMember: (
    projectMember: IProjectMemberView,
    companyId: number
  ) => ReturnType<typeof removeProjectMemberFn>;
  updateProjectMember: (
    projectMember: IProjectMemberView,
    companyId: number
  ) => ReturnType<typeof updateProjectMemberFn>;
  removeMemberFromCompany: (
    companyMember: ICompanyMember
  ) => ReturnType<typeof removeMemberFromCompanyFn>;
  exportCompanyMembers: (
    companyId: number
  ) => ReturnType<typeof exportMembersFn>;
}

type IOwnProps = ComponentProps & IDispatchToProps;

const CompanyMemberModal: React.FC<IOwnProps> = (props) => {
  const [dirty, setDirty] = useState(false);
  const [roleDirty, setRoleDirty] = useState(false);
  const [name, setName] = useState(props.user.name);
  const [phone, setPhone] = useState(props.user.phoneNumber || '');
  const [newSignatureFile, setNewSignatureFile] = useState<unknown | null>(
    null
  );
  const [removeSignature, setRemoveSignature] = useState(false);
  const [admin, setAdmin] = useState(props.companyMember.admin);
  const [selectedRolesObject, setSelectedRolesObject] = useState(
    props.companyMember.roles
  );
  const [selectedTab, setSelectedTab] = useState(
    'info' as 'info' | 'projects' | 'changeLog'
  );
  const [loading, setLoading] = useState(false);
  const [memberLogs, setMemberLogs] = useState([] as CompanyMemberLog[]);
  const [employeeId, setEmployeeId] = useState<string>(
    props.companyMember.employeeId || ''
  );
  const [loadingMemberLogs, setLoadingMemberLogs] = useState(false);
  const [removeing, setRemoveing] = useState(false);

  const isCompanyOwner =
    props.companyObject.owner.userId === props.companyMember.userId;
  const onClose = () => {
    if (
      !dirty ||
      confirm(i18n.t('confirmCloseMember', { ns: 'companyManagement' }))
    )
      props.onClose();
  };

  function loadMoreLogs() {
    setLoadingMemberLogs(true);
    props
      .getMemberLogs(
        props.user.id,
        props.companyMember.companyId,
        memberLogs.length > 0 ? memberLogs[memberLogs.length - 1]?.id : null,
        100
      )
      .then((result) => {
        setMemberLogs([...memberLogs, ...result]);
      })
      .catch(() => {
        notify(
          i18n.t('errorLoadingMemberLogs', { ns: 'companyManagement' }),
          'error',
          false
        );
      })
      .finally(() => {
        setLoadingMemberLogs(false);
      });
  }

  useEffect(() => {
    loadMoreLogs();
  }, [props.user.id]);

  const saveMember = () => {
    const calls = [] as Promise<any>[];
    if (removeSignature && props.companyMember.signature)
      calls.push(
        props.deleteSignature(
          props.companyObject.id,
          props.companyMember.userId
        )
      );
    if (newSignatureFile)
      calls.push(
        props.addSignature(
          props.companyObject.id,
          props.companyMember.userId,
          newSignatureFile
        )
      );
    if (name !== props.user.name) {
      calls.push(
        props.changePhone(
          props.companyMember.userId,
          props.user.phoneNumber ? props.user.phoneNumber : null,
          props.user.phoneNumber,
          name
        )
      );
    }

    const toAddRoleIds = Object.values(selectedRolesObject || {})
      .filter((it) => !it.sourceRoleId)
      .filter(
        (it) =>
          !props.companyMember.roles[it.id] ||
          (props.companyMember.roles[it.id].sourceRoleId &&
            it.id !== props.companyMember.roles[it.id].sourceRoleId)
      )
      .map((it) => it.id);

    const toRemoveRoles = Object.entries(props.companyMember.roles || {})
      .filter(([key, value]) => {
        return (
          (Number(key) === value.sourceRoleId || !value.sourceRoleId) &&
          !selectedRolesObject[key]
        );
      })
      .map((it) => {
        return Number(it[0]);
      });

    if (toAddRoleIds.length > 0) {
      calls.push(
        props.addRoles(
          toAddRoleIds.map((it) => ({ roleId: it, userId: props.user.id })),
          props.companyObject.id
        )
      );
    }
    if (toRemoveRoles.length > 0) {
      calls.push(
        props.deleteRoles(
          toRemoveRoles.map((it) => ({ roleId: it, userId: props.user.id })),
          props.companyObject.id
        )
      );
    }

    calls.push(
      props.updateMember({
        ...props.companyMember,
        ...(admin !== props.companyMember.admin ? { admin } : {}),
        employeeId: employeeId,
      })
    );

    setLoading(true);
    Promise.all(calls)
      .then(() => {
        if (phone !== props.user.phoneNumber && phone.length === 11) {
          props
            .changePhone(
              props.companyMember.userId,
              props.user.phoneNumber ? props.user.phoneNumber : null,
              phone,
              props.user.name
            )
            .catch(() => {
              notify(
                i18n.t('unauthorizedChangeUserPhone', {
                  ns: 'companyManagement',
                }),
                'error'
              );
            });
        }
      })
      .finally(() => {
        props.getMember(props.user.id, props.companyObject.id).finally(() => {
          setLoading(false);
          setDirty(false);
          setNewSignatureFile(null);
          setRemoveSignature(false);
          props.onClose();
        });
      });
  };

  const selectedRoleOptions = [] as IOption[];
  const implicitRoleOptoins = [] as IOption[];
  const roleOptions = [] as IOption[];
  let hasDistrict = false;
  let hasPost = false;
  Object.values(props.companyObject.roles).forEach((it) => {
    if (it.roleType === 'InProject') return;

    const ret = { value: it.id, label: it.displayName } as IOption;
    if (selectedRolesObject[it.id.toString()]) {
      if (
        !selectedRolesObject[it.id.toString()].sourceRoleId ||
        selectedRolesObject[it.id.toString()].sourceRoleId === it.id
      )
        selectedRoleOptions.push(ret);
      else implicitRoleOptoins.push(ret);
      if (it.roleType === 'District' || it.roleType === 'Compound')
        hasDistrict = true;
      if (it.roleType === 'Compound' || it.roleType === 'Post') hasPost = true;
    }
    if (!it.compoundCreationWithRoleId) roleOptions.push(ret);
  });

  function getExtraDescription(logItem: CompanyMemberLog) {
    switch (logItem.action) {
      case 'AddedToCompany':
        return '';
      case 'RemovedFromCompany':
        return '';
      case 'AddRole':
        return props.companyObject.roles[Number(logItem.props.newValue)]
          ?.displayName;
      case 'RemoveRole':
        return props.companyObject.roles[Number(logItem.props.oldValue)]
          ?.displayName;
      case 'UpdateName':
        return messages.fromTo(logItem.props.oldValue, logItem.props.newValue);
      case 'UpdatePhone':
        return messages.fromTo(
          convertDigitToProperFormat(logItem.props.oldValue),
          convertDigitToProperFormat(logItem.props.newValue)
        );
      default:
        return '';
    }
  }

  const handleDelete = () => {
    const result = window.confirm(
      messages.deleteUserFromCompany(props.user.name)
    );
    if (result) {
      setRemoveing(true);
      const companyMember = {
        userId: props.user.id,
        companyId: Number(props.companyObject.id),
      } as ICompanyMember;
      props.removeMemberFromCompany(companyMember).then(
        () => {
          notify(messages.memberRemoved(props.user.name));
          setRemoveing(false);
          onClose();
        },
        (err: any) => {
          setRemoveing(false);
          display(err);
        }
      );
    }
  };
  const handleRestore = () => {
    const result = window.confirm(
      messages.restoreUserInCompany(props.user.name)
    );
    if (result) {
      setRemoveing(true);
      props.addMember({ ...props.companyMember, inactive: false }).then(
        () => {
          notify(messages.memberRemoved(props.user.name));
          setRemoveing(false);
        },
        (err: any) => {
          setRemoveing(false);
          display(err);
        }
      );
    }
  };

  const missingInfo =
    !name ||
    name.length === 0 ||
    (phone && !phone.match(/0[0-9]{10}/)) ||
    (!phone && !!props.user.phoneNumber);

  function getTable(implicit: boolean) {
    return (
      <table className="highlightedTable">
        <tr>
          <th style={{ textAlign: `${dir === 'rtl' ? 'right' : 'left'}` }}>
            {
              i18n.t('projectName', {
                ns: 'companyManagement',
              }) as string
            }
          </th>
          <th style={{ textAlign: `${dir === 'rtl' ? 'right' : 'left'}` }}>
            {
              i18n.t('relatedRole', {
                ns: 'companyManagement',
              }) as string
            }
          </th>
          <th style={{ textAlign: `${dir === 'rtl' ? 'right' : 'left'}` }}>
            {
              i18n.t('projectRole', {
                ns: 'companyManagement',
              }) as string
            }
          </th>
        </tr>
        {props.projectMembers
          .filter((a) => a.implicit === implicit)
          .sort((a, b) => {
            const atype = props.projects[a.projectId]?.tags?.[0];
            const btype = props.projects[b.projectId]?.tags?.[0];
            if (!atype) return 1;
            if (!btype) return -1;
            return atype.localeCompare(btype);
          })
          ?.map((pm: IProjectMemberView, index, list) => {
            let header = <></>;
            let showHeader = false;
            const currentType = props.projects[pm.projectId]?.tags?.[0];
            if (index === 0) showHeader = true;
            else {
              const prevType =
                props.projects[list[index - 1].projectId]?.tags?.[0];
              if (prevType !== currentType) showHeader = true;
            }
            if (showHeader)
              header = (
                <tr>
                  <th
                    colSpan={3}
                    style={{ textAlign: `${dir === 'rtl' ? 'right' : 'left'}` }}
                  >
                    {currentType ||
                      (i18n.t('other', {
                        ns: 'companyManagement',
                      }) as string)}
                  </th>
                </tr>
              );
            return (
              pm && (
                <>
                  {header}
                  <tr key={`${pm.userId} ${pm.roleId}`}>
                    <td
                      style={{
                        textAlign: `${dir === 'rtl' ? 'right' : 'left'}`,
                      }}
                    >
                      {props.projects[pm.projectId]?.name}
                    </td>
                    <td
                      style={{
                        textAlign: `${dir === 'rtl' ? 'right' : 'left'}`,
                      }}
                    >
                      {pm.roleId
                        ? (props.companyObject.roles[pm.roleId]?.displayName ??
                          'roleId:' + pm.roleId)
                        : pm.relatedRoleId
                          ? (props.companyObject.roles[pm.relatedRoleId]
                              ?.displayName ?? 'roleId:' + pm.relatedRoleId)
                          : '-'}
                    </td>
                    <td
                      style={{
                        textAlign: `${dir === 'rtl' ? 'right' : 'left'}`,
                      }}
                    >
                      {pm.projectRoleId
                        ? props.companyObject.roles[pm.projectRoleId]
                            .displayName
                        : '-'}
                    </td>
                    {/* <td>
                        {pm.implicit
                            ? withTitle(
                                <span>{messages.implictUser}</span>,
                                messages.implicitDesc
                            )
                            : messages.mainUser}
                      </td>*/}
                  </tr>
                </>
              )
            );
          })}
      </table>
    );
  }

  const dir = languageDirection();

  return (
    <Modal
      tabs={modalTabs}
      selectedTab={selectedTab}
      title={props.user.name}
      onClose={onClose}
      isActive={true}
      width="700px"
      onSelectATab={setSelectedTab as any}
    >
      {selectedTab === 'info' && (
        <>
          <div className="flex">
            <div className="m-3 flex-1">
              {withTitle(
                <Input
                  id="name"
                  type={'text'}
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setDirty(true);
                  }}
                  disabled={props.companyMember.inactive}
                  label={
                    i18n.t('name', {
                      ns: 'companyManagement',
                    }) as string
                  }
                />,
                i18n.t('nameHint', {
                  ns: 'companyManagement',
                }) as string,
                'bottom'
              )}
              <Input
                id="phone"
                type={'tel'}
                value={phone}
                maxLength={11}
                onChange={(e) => {
                  setPhone(englishNumber(e.target.value));
                  setDirty(true);
                }}
                disabled={props.companyMember.inactive}
                labelStyle={{ color: black }}
                label={
                  i18n.t('phoneNumber', {
                    ns: 'companyManagement',
                  }) as string
                }
              />
              <Input
                id="employeeId"
                type="text"
                value={employeeId}
                onChange={(e) => {
                  if (/^[A-Za-z0-9]*$/.test(e.target.value)) {
                    setEmployeeId(e.target.value);
                  }
                }}
                disabled={props.companyMember.inactive}
                label={
                  i18n.t('employeeId', {
                    ns: 'companyManagement',
                  }) as string
                }
              />
            </div>
            <div className="m-3">
              <MemberSignature
                member={props.companyMember}
                addSignature={(file) => {
                  setNewSignatureFile(file);
                  setRemoveSignature(false);
                  setDirty(true);
                  return Promise.resolve();
                }}
                removeSignature={() => {
                  setNewSignatureFile(null);
                  setRemoveSignature(true);
                  setDirty(true);
                  return Promise.resolve();
                }}
                revert={() => {
                  setNewSignatureFile(null);
                  setRemoveSignature(false);
                }}
                removing={removeSignature}
              />
            </div>
          </div>
          <Select
            values={selectedRoleOptions}
            options={roleOptions}
            onChangeValues={(values) => {
              const newRoles = values.reduce((current, item) => {
                current[item.value] = { id: item.value, name: item.label };
                return current;
              }, {});
              setSelectedRolesObject(newRoles);
              setDirty(true);
              setRoleDirty(true);
            }}
            multiChoice={true}
            label={
              i18n.t('roles', {
                ns: 'companyManagement',
              }) as string
            }
            disabled={props.companyMember.inactive}
          />
          <BadgeGroup className="my-4 mx-2 justify-start">
            <div style={{ fontSize: '12px', color: 'gray' }}>
              {
                i18n.t('otherRoles', {
                  ns: 'companyManagement',
                }) as string
              }
              :{' '}
            </div>
            {implicitRoleOptoins.map((it) => (
              <Badge color={'gray'} className="mx-1" key={it.value}>
                {it.label}
              </Badge>
            ))}
            {roleDirty &&
              (i18n.t('shownAfterSave', {
                ns: 'companyManagement',
              }) as string)}
          </BadgeGroup>
          {(isCompanyOwner || props.isSupport) && (
            <Input
              disabled={props.companyMember.inactive}
              checked={admin}
              id={props.companyMember.userId?.toString()}
              label={
                i18n.t('admin', {
                  ns: 'companyManagement',
                }) as string
              }
              name="isAdmin"
              onChange={(e) => setAdmin(e.target.checked)}
              type="checkbox"
            />
          )}
          {(!hasDistrict || !hasPost) && (
            <div
              style={{
                border: `solid 1px ${red}`,
                padding: '10px',
                marginTop: '10px',
                borderRadius: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <div>
                <Icon type="info" color={red} />
              </div>
              <div>
                {!hasDistrict && !props.companyMember.inactive && (
                  <li style={{ color: red }}>
                    {
                      i18n.t('specifyDistrict', {
                        ns: 'companyManagement',
                      }) as string
                    }
                  </li>
                )}
                {!hasPost && !props.companyMember.inactive && (
                  <li style={{ color: red }}>
                    {
                      i18n.t('specifyPost', {
                        ns: 'companyManagement',
                      }) as string
                    }
                  </li>
                )}
              </div>
            </div>
          )}
          <div className="flex-grow" />
          <div className="flex">
            <div>
              {props.companyMember.inactive ? (
                <Button
                  color={'red-outline'}
                  onClick={handleRestore}
                  loading={removeing}
                >
                  <Icon type="rotate-ccw" color={red} />
                  {
                    i18n.t('restore', {
                      ns: 'companyManagement',
                    }) as string
                  }
                </Button>
              ) : (
                <Button
                  color={'red-outline'}
                  onClick={handleDelete}
                  loading={removeing}
                >
                  <Icon type="trash" color={red} />
                  {
                    i18n.t('disable', {
                      ns: 'companyManagement',
                    }) as string
                  }
                </Button>
              )}
            </div>
            <div className="flex-grow" />

            <Button
              loading={loading}
              onClick={saveMember}
              color={'green'}
              disabled={missingInfo}
            >
              {
                i18n.t('save', {
                  ns: 'companyManagement',
                }) as string
              }
            </Button>
          </div>
        </>
      )}
      {selectedTab === 'changeLog' && (
        <div>
          {loadingMemberLogs ? (
            <div className="progress">
              <div className="indeterminate" />
            </div>
          ) : (
            <table
              className="highlightedTable"
              style={{
                width: '100%',
                textAlign: dir === 'rtl' ? 'right' : 'left',
              }}
            >
              <thead>
                <tr
                  style={{
                    position: 'sticky',
                  }}
                >
                  <th
                    style={{ textAlign: `${dir === 'rtl' ? 'right' : 'left'}` }}
                  >
                    {
                      i18n.t('action', {
                        ns: 'companyManagement',
                      }) as string
                    }
                  </th>
                  <th
                    style={{ textAlign: `${dir === 'rtl' ? 'right' : 'left'}` }}
                  >
                    {
                      i18n.t('date', {
                        ns: 'companyManagement',
                      }) as string
                    }
                  </th>
                  <th
                    style={{ textAlign: `${dir === 'rtl' ? 'right' : 'left'}` }}
                  >
                    {
                      i18n.t('otherDesc', {
                        ns: 'companyManagement',
                      }) as string
                    }
                  </th>
                  <th
                    style={{ textAlign: `${dir === 'rtl' ? 'right' : 'left'}` }}
                  >
                    {
                      i18n.t('actor', {
                        ns: 'companyManagement',
                      }) as string
                    }
                  </th>
                </tr>
              </thead>
              {memberLogs.map((logItem) => {
                return (
                  <tr key={logItem.id}>
                    <td
                      style={{
                        textAlign: `${dir === 'rtl' ? 'right' : 'left'}`,
                      }}
                    >
                      {
                        i18n.t(`${[logItem.action!]}`, {
                          ns: 'companyManagement',
                        }) as string
                      }
                    </td>
                    <td
                      style={{
                        textAlign: `${dir === 'rtl' ? 'right' : 'left'}`,
                      }}
                    >
                      {smartDateString(
                        logItem.actionDate!,
                        true,
                        false,
                        true,
                        undefined,
                        true
                      )}
                    </td>
                    <td
                      style={{
                        textAlign: `${dir === 'rtl' ? 'right' : 'left'}`,
                      }}
                    >
                      {getExtraDescription(logItem)}
                    </td>
                    <td
                      style={{
                        textAlign: `${dir === 'rtl' ? 'right' : 'left'}`,
                      }}
                    >
                      {logItem.actorUserId === logItem.userId
                        ? (i18n.t('selfuser', {
                            ns: 'companyManagement',
                          }) as string)
                        : !logItem.actorUserId
                          ? '-'
                          : props.users[logItem.actorUserId]?.name ||
                            'U' + logItem.actorUserId}
                    </td>
                  </tr>
                );
              })}
            </table>
          )}
        </div>
      )}
      {selectedTab === 'projects' && (
        <div style={{ overflow: 'overlay' }}>
          {getTable(false)}
          <Collapsible
            header={
              i18n.t('implicitDesc', {
                ns: 'companyManagement',
              }) as string
            }
          >
            {getTable(true)}
          </Collapsible>
        </div>
      )}
    </Modal>
  );
};

const mapDispatchToProps: MapDispatchToProps<
  IDispatchToProps,
  IOwnProps
> = () => {
  return {
    addRoles: (userRoles: IUserRoleView[], companyId: number) =>
      appDispatch(addUserRoles({ userRoles, companyId })),
    deleteRoles: (userRoles: IUserRoleView[], companyId: number) =>
      appDispatch(deleteUserRoles({ userRoles, companyId })),
    addSignature: (companyId: number, userId: number, file: any) =>
      appDispatch(addMemberSignature({ companyId, userId, file })),
    deleteSignature: (companyId: number, userId: number) =>
      appDispatch(removeMemberSignature({ companyId, userId })),
    updateMember: (member: ICompanyMember) =>
      appDispatch(updateCompanyMember({ companyMember: member })),
    getMember: (userId, companyId) =>
      appDispatch(getCompanyMember({ userId, companyId })),
    getMemberLogs: (
      userId: number,
      companyId: number,
      beforeId: number | null,
      count: number
    ) =>
      appDispatch(getCompanyMemberLogs({ userId, companyId, beforeId, count })),
    addMember: (member: ICompanyMember) =>
      appDispatch(addMemberToCompany({ companyMember: member })),
    removeProjectMember: (
      projectMember: IProjectMemberView,
      companyId: number
    ) => appDispatch(removeProjectMember({ projectMember, companyId })),
    updateProjectMember: (
      projectMember: IProjectMemberView,
      companyId: number
    ) => appDispatch(updateProjectMember({ projectMember, companyId })),
    removeMemberFromCompany: (companyMember: ICompanyMember) =>
      appDispatch(removeMemberFromCompany({ companyMember })),
    exportCompanyMembers: (companyId: number) =>
      appDispatch(exportMembers({ companyId })),
    changePhone: (
      userId: number,
      currentPhone: string | null,
      newPhone: string | null,
      newName?: string
    ) =>
      appDispatch(
        changeUserPhone({
          username: '',
          userId: userId.toString(),
          currentPhone: currentPhone!,
          newPhone: newPhone!,
          swapIfExists: true,
          newName,
        })
      ),
  };
};

export default connect(null, mapDispatchToProps)(CompanyMemberModal);
